import { DataTable, TruncCell } from "@/components/common/DataTable";
import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, ScanText, ExternalLink } from "lucide-react";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";

interface BrandRow { id: string; name: string }
interface Decoder { brand: string; kind: string; source_field: string | null; active: boolean }
interface DisambigRow { brand: string; active: boolean }
interface AliasRow { brand: string; active: boolean }

export default function DecoderCoverage() {
  const { t } = useTranslation("admin");
  const { isSuperadmin: isAdmin, isLoading } = useIsSuperadmin();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);

  const brandsQ = useQuery({
    queryKey: ["decoder-coverage", "brands"],
    queryFn: async () => {
      const { data, error } = await supabase.from("brands").select("id,name").order("name");
      if (error) throw error;
      return (data ?? []) as BrandRow[];
    },
  });
  const decodersQ = useQuery({
    queryKey: ["decoder-coverage", "decoders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_date_decoders")
        .select("brand,kind,source_field,active");
      if (error) throw error;
      return (data ?? []) as Decoder[];
    },
  });
  const disambigQ = useQuery({
    queryKey: ["decoder-coverage", "disambig"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_decoding_rules")
        .select("brand,active");
      if (error) throw error;
      return (data ?? []) as DisambigRow[];
    },
  });
  const aliasesQ = useQuery({
    queryKey: ["decoder-coverage", "aliases"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_field_aliases")
        .select("brand,active");
      if (error) throw error;
      return (data ?? []) as AliasRow[];
    },
  });

  const rows = useMemo(() => {
    const brands = brandsQ.data ?? [];
    const decoders = decodersQ.data ?? [];
    const disambig = disambigQ.data ?? [];
    const aliases = aliasesQ.data ?? [];

    const byBrand = (arr: { brand: string }[]) => {
      const m = new Map<string, typeof arr>();
      for (const r of arr) {
        const k = r.brand.toUpperCase();
        if (!m.has(k)) m.set(k, [] as any);
        (m.get(k) as any).push(r);
      }
      return m;
    };
    const decMap = byBrand(decoders);
    const disMap = byBrand(disambig);
    const aliMap = byBrand(aliases);

    return brands.map((b) => {
      const key = b.name.toUpperCase();
      const decs = (decMap.get(key) ?? []) as Decoder[];
      const activeDec = decs.find((d) => d.active) ?? null;
      const inactiveDec = !activeDec ? decs[0] ?? null : null;
      const disAll = (disMap.get(key) ?? []) as DisambigRow[];
      const aliAll = (aliMap.get(key) ?? []) as AliasRow[];
      const status: "active" | "inactive" | "missing" = activeDec
        ? "active"
        : inactiveDec
        ? "inactive"
        : "missing";
      return {
        id: b.id,
        name: b.name,
        kind: activeDec?.kind ?? inactiveDec?.kind ?? null,
        sourceField: activeDec?.source_field ?? inactiveDec?.source_field ?? null,
        activeDisambig: disAll.filter((r) => r.active).length,
        activeAliases: aliAll.filter((r) => r.active).length,
        status,
      };
    });
  }, [brandsQ.data, decodersQ.data, disambigQ.data, aliasesQ.data]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyMissing && r.status === "active") return false;
      if (f && !r.name.toLowerCase().includes(f)) return false;
      return true;
    });
  }, [rows, filter, onlyMissing]);

  const summary = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.status === "active").length;
    const missing = rows.filter((r) => r.status === "missing").length;
    const disambigCount = (disambigQ.data ?? []).filter((r) => r.active).length;
    const aliasCount = (aliasesQ.data ?? []).filter((r) => r.active).length;
    return { total, active, missing, disambigCount, aliasCount };
  }, [rows, disambigQ.data, aliasesQ.data]);

  if (isLoading) return null;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["decoder-coverage"] });
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-3 mb-2">
          <SettingsBackLink to="/platform/brands" label={t("nav.brands", { ns: "settings" })} />
          <div className="flex items-center gap-2">
            <ScanText className="w-5 h-5 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">
              {t("decoderCoverage.title")}
            </h1>
          </div>
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={refresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("decoderCoverage.refresh")}
            </Button>
          </div>
        </div>
        <p className="text-muted-foreground">{t("decoderCoverage.description")}</p>
      </div>

      <div className="px-6 grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <SummaryChip label={t("decoderCoverage.summary.totalBrands")} value={summary.total} />
        <SummaryChip label={t("decoderCoverage.summary.withDecoder")} value={summary.active} tone="ok" />
        <SummaryChip label={t("decoderCoverage.summary.missing")} value={summary.missing} tone="warn" />
        <SummaryChip label={t("decoderCoverage.summary.disambigRules")} value={summary.disambigCount} />
        <SummaryChip label={t("decoderCoverage.summary.fieldAliases")} value={summary.aliasCount} />
      </div>

      <div className="px-6 flex flex-wrap items-center gap-4 mb-4">
        <Input
          placeholder={t("decoderCoverage.filterPlaceholder")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
        />
        <div className="flex items-center gap-2">
          <Switch id="only-missing" checked={onlyMissing} onCheckedChange={setOnlyMissing} />
          <Label htmlFor="only-missing" className="text-sm">
            {t("decoderCoverage.onlyMissing")}
          </Label>
        </div>
      </div>

      <div className="px-6">
        <DataTable<(typeof filtered)[number]>
          storageKey="decoder-coverage-column-widths"
          rows={filtered}
          rowKey={(r) => r.id}
          empty={t("decoderCoverage.empty")}
          defaultSort={{ key: "brand", asc: true }}
          columns={[
            { key: "brand", header: t("decoderCoverage.columns.brand"), width: 180, sortValue: (r) => r.name.toLowerCase(), cell: (r) => <TruncCell className="font-medium">{r.name}</TruncCell> },
            { key: "kind", header: t("decoderCoverage.columns.kind"), width: 130, sortValue: (r) => r.kind, cell: (r) => <TruncCell className="font-mono text-xs">{r.kind ?? "—"}</TruncCell> },
            { key: "source", header: t("decoderCoverage.columns.source"), width: 170, sortValue: (r) => r.sourceField, cell: (r) => <TruncCell className="font-mono text-xs">{r.sourceField ?? "—"}</TruncCell> },
            { key: "disambig", header: t("decoderCoverage.columns.disambig"), width: 130, align: "right", sortValue: (r) => r.activeDisambig, cell: (r) => <span className="tabular-nums">{r.activeDisambig}</span> },
            { key: "aliases", header: t("decoderCoverage.columns.aliases"), width: 110, align: "right", sortValue: (r) => r.activeAliases, cell: (r) => <span className="tabular-nums">{r.activeAliases}</span> },
            { key: "status", header: t("decoderCoverage.columns.status"), width: 120, sortValue: (r) => ({ missing: 0, inactive: 1, active: 2 })[r.status], cell: (r) => <StatusPill status={r.status} t={t} /> },
          ]}
          actionsWidth={96}
          actions={(r) => (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/decoding?brand=${encodeURIComponent(r.name)}`)}>
              {t("decoderCoverage.edit")}<ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          )}
        />
      </div>
    </div>
  );
}

function SummaryChip({ label, value, tone }: { label: string; value: number; tone?: "ok" | "warn" }) {
  const toneClass =
    tone === "ok"
      ? "border-emerald-500/30 bg-emerald-500/5"
      : tone === "warn"
      ? "border-amber-500/30 bg-amber-500/5"
      : "border-border bg-card";
  return (
    <div className={`rounded-xl border ${toneClass} p-4`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function StatusPill({ status, t }: { status: "active" | "inactive" | "missing"; t: (k: string) => string }) {
  if (status === "active")
    return <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 border-0">{t("decoderCoverage.status.active")}</Badge>;
  if (status === "inactive")
    return <Badge variant="outline">{t("decoderCoverage.status.inactive")}</Badge>;
  return <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/15 border-0">{t("decoderCoverage.status.missing")}</Badge>;
}
